import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  wasteCollectionSchema,
  generateDefaultPickupDate,
  type WasteCollectionFormData,
} from '../validation/waste-collection.schemas';
import { wasteCollectionApi } from '../api/waste-collection.api';
import { schoolsApi } from '@/domains/schools/api/schools.api';
import { toastOneSuccess, toastOneError } from '@/toast';
import { applyServerFieldErrors } from '@/shared/utils';
import {
  adresaInitialaDeColectare,
  adresaPentruFormular,
  adresaScoliiEIncompleta,
} from '../validation/adresa-colectare';

/**
 * useWasteCollectionWithForm - Form hook for waste collection form
 *
 * Features:
 * - Automatic address population from school data
 * - Client-side validation with Zod
 * - Backend error mapping
 * - Form reset after successful submission
 * - Loading states
 *
 * Context7 patterns:
 * - watch() + useEffect() + setValue() for dependent fields
 * - setError() for backend error mapping (supports nested paths)
 * - reset() with fresh defaults after submission
 * - Direct API submission (no mapping functions!)
 *
 * @example
 * const { form, onSubmit, loading } = useWasteCollectionWithForm();
 *
 * return (
 *   <Form {...form}>
 *     <form onSubmit={onSubmit}>
 *       // form fields
 *     </form>
 *   </Form>
 * );
 */
export function useWasteCollectionWithForm() {
  // Fetch school data for address auto-populate
  const {
    data: ownSchoolData,
    isLoading: isLoadingSchool,
    isError: isSchoolError,
    refetch: refetchSchool,
  } = useQuery({
    queryKey: ['schools', 'own'],
    queryFn: () => schoolsApi.getOwn(),
  });

  // Fetch default form values
  const {
    data: defaultValues,
    refetch: refetchDefaults,
    isLoading: isLoadingDefaults,
    isError: isDefaultsError,
  } = useQuery({
    queryKey: ['waste-collection', 'defaults'],
    queryFn: () => wasteCollectionApi.getDefault(),
  });

  // Un 500/retea la incarcarea datelor scolii sau a valorilor implicite NU mai randa tacut
  // un formular gol, ca si cum scoala n-ar avea inca date (precedent use-editions.ts:27-35,
  // #191) - se afiseaza eroarea, cu reincercare directa pe interogarile care au picat.
  const isError = isSchoolError || isDefaultsError;
  const refetch = () => {
    if (isSchoolError) refetchSchool();
    if (isDefaultsError) refetchDefaults();
  };

  // Initialize form with Zod validation
  const form = useForm<WasteCollectionFormData>({
    resolver: zodResolver(wasteCollectionSchema),
    defaultValues: {
      // Context7 Pattern: Only static defaults, NO spreading API data
      // API data loaded via form.reset() in useEffect below
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      dataOrientativaColectare: generateDefaultPickupDate(),
      pickupAddressSameWithSchoolAddress: true,
      pickupAddress: {
        judet: '',
        oras: '',
        comuna: '',
        sat: '',
        strada: '',
        numar: '',
        codPostal: '',
        bloc: '',
        scara: '',
        etaj: '',
        apartament: '',
      },
      deeeKg: undefined,
      dbaKg: undefined,
      becuriSiNeoaneKg: undefined,
      photos: [],
      consent: false,
    },
    mode: 'onBlur',
  });

  // Update form defaults when API data loads
  // Context7 Pattern: Explicit null coalescing for all fields
  useEffect(() => {
    if (defaultValues) {
      form.reset({
        // Contact fields with null coalescing
        contactPersonName: defaultValues.contactPersonName ?? '',
        contactPersonEmail: defaultValues.contactPersonEmail ?? '',
        contactPersonPhone: defaultValues.contactPersonPhone ?? '',

        // Override API date with fresh default
        dataOrientativaColectare: generateDefaultPickupDate(),

        // Toggle starts checked (auto-fill school address)
        pickupAddressSameWithSchoolAddress: true,

        // Bifa porneste pusa, deci adresa scolii intra chiar in valorile de reset: «Resetare»
        // (`form.reset()` fara argumente) revine la ele, iar efectul de copiere de mai jos nu
        // ruleaza din nou cat timp bifa nu se schimba. Vezi `adresa-colectare.ts`.
        pickupAddress: adresaInitialaDeColectare(ownSchoolData?.adresa, defaultValues.pickupAddress),

        // Number fields preserve undefined (optional)
        deeeKg: defaultValues.deeeKg ?? undefined,
        dbaKg: defaultValues.dbaKg ?? undefined,
        becuriSiNeoaneKg: defaultValues.becuriSiNeoaneKg ?? undefined,

        // Always reset photos and consent
        photos: [],
        consent: false,
      });
    }
  }, [defaultValues, ownSchoolData, form]);

  // Context7 Pattern: Watch address toggle for auto-populate
  const sameAsSchool = form.watch('pickupAddressSameWithSchoolAddress');

  // Context7 Pattern: Auto-populate address when toggle changes
  useEffect(() => {
    if (sameAsSchool === true && ownSchoolData?.adresa) {
      // Copy school address to pickup address (bifa repusa dupa ce a fost scoasa)
      form.setValue('pickupAddress', adresaPentruFormular(ownSchoolData.adresa));
    }
    // Note: Keep school address when switching to false (user can edit)
  }, [sameAsSchool, ownSchoolData, form]);

  // Submission mutation
  const mutation = useMutation({
    mutationFn: async (data: WasteCollectionFormData) => {
      console.log('[useWasteCollectionWithForm] Starting mutation with data:', data);

      // Transform payload: Remove pickupAddress.id when same as school address
      // This prevents backend "detached entity" errors when copying school address
      const payload = { ...data };
      if (payload.pickupAddressSameWithSchoolAddress && payload.pickupAddress?.id) {
        const { id, ...addressWithoutId } = payload.pickupAddress;
        payload.pickupAddress = addressWithoutId as any;
        console.log('[useWasteCollectionWithForm] Removed pickupAddress.id from payload (school address copy)');
      }

      return wasteCollectionApi.create(payload as any);
    },
    onSuccess: async () => {
      toastOneSuccess('Formularul a fost trimis cu succes!');

      // Fetch fresh defaults
      const result = await refetchDefaults();

      // Context7 Pattern: Explicit null coalescing for all fields
      form.reset({
        // Contact fields with null coalescing
        contactPersonName: result.data?.contactPersonName ?? '',
        contactPersonEmail: result.data?.contactPersonEmail ?? '',
        contactPersonPhone: result.data?.contactPersonPhone ?? '',

        // Override API date with fresh default
        dataOrientativaColectare: generateDefaultPickupDate(),

        // Toggle starts checked (auto-fill school address)
        pickupAddressSameWithSchoolAddress: true,

        // Aceeasi regula ca la incarcare: bifa ramane pusa, deci adresa scolii vine cu ea.
        pickupAddress: adresaInitialaDeColectare(ownSchoolData?.adresa, result.data?.pickupAddress),

        // Number fields preserve undefined (optional)
        deeeKg: result.data?.deeeKg ?? undefined,
        dbaKg: result.data?.dbaKg ?? undefined,
        becuriSiNeoaneKg: result.data?.becuriSiNeoaneKg ?? undefined,

        // Always reset photos and consent
        photos: [],
        consent: false,
      });
    },
    onError: (error) => {
      // Context7 Pattern: Map backend errors to form fields
      // Supports nested paths like "pickupAddress.judet"
      applyServerFieldErrors(error, form.setError);

      // Show generic error message
      if (error.message) {
        toastOneError(error.message);
      } else {
        toastOneError('A apărut o eroare la trimiterea formularului');
      }
    },
  });

  // Submit handler
  //
  // NU lasa respingerea mutatiei sa iasa de aici: `onError` de mai sus a aratat deja toastul si
  // erorile pe campuri, iar promisiunea respinsa urca prin dialogul de confirmare pana la un
  // `onClick` care n-o asteapta — adica `unhandledrejection` in telemetrie (07.10, acelasi tipar ca
  // la profil, `70501e20`). Rezultatul se citeste din valoarea intoarsa: `false` = serverul a
  // respins trimiterea, iar pagina tine dialogul deschis; `true` in rest (trimis, sau oprit de
  // validarea de pe client, care isi are propriul semnal).
  const handleSubmit = async (): Promise<boolean> => {
    let respins = false;
    await form.handleSubmit(
      async (data) => {
        console.log('[useWasteCollectionWithForm] Validation passed, submitting data:', data);
        try {
          await mutation.mutateAsync(data);
        } catch {
          respins = true;
        }
      },
      (errors) => {
        console.error('[useWasteCollectionWithForm] Validation failed with errors:', errors);
      }
    )();
    return !respins;
  };

  return {
    form,
    onSubmit: handleSubmit,
    loading: mutation.isPending || isLoadingSchool || isLoadingDefaults,
    isSubmitting: mutation.isPending,
    isDirty: form.formState.isDirty, // Track unsaved changes for navigation blocking
    ownSchoolData,
    // Doar dupa ce datele scolii au sosit: pana atunci nu stim daca adresa lipseste sau se incarca.
    adresaScoliiIncompleta: Boolean(ownSchoolData) && adresaScoliiEIncompleta(ownSchoolData?.adresa),
    isError,
    refetch,
  };
}
